import { COUNTRIES_MAP } from '@/const/countries.const';
import { PermissionType } from '@/enums/PermissionType';
import {
  HostToolError,
  type ICartadigitalAdminHostToolDeps,
  type IGpthelpToolExecCtx,
  type IGpthelpToolScope,
} from './tool-types';

/**
 * Opaque scope key handed to the gpthelp widget (`scope-key` attribute) and
 * echoed back in every tool request of a turn. Conversations are persisted
 * per key, so switching restaurant/branch never mixes chats or data.
 */
export function buildScopeKey(scope: {
  restaurantId: number;
  branchId: number;
}): string {
  return `r${scope.restaurantId}-b${scope.branchId}`;
}

export function ensureRestaurantScopeOrThrow(
  deps: ICartadigitalAdminHostToolDeps,
): IGpthelpToolScope {
  const restaurant = deps.restaurantStore.restaurant;
  const branch = deps.restaurantStore.selectedBranch;

  if (!restaurant || !branch) {
    throw new HostToolError('NO_SCOPE', 'No restaurant/branch selected');
  }

  const restaurantId = restaurant.id;
  const branchId = branch.id;
  if (typeof restaurantId !== 'number' || typeof branchId !== 'number') {
    throw new HostToolError('NO_SCOPE', 'No restaurant/branch selected');
  }

  const timeZone =
    branch.location?.tz ??
    COUNTRIES_MAP.get(restaurant.country ?? 'AR')?.tz ??
    'America/Argentina/Buenos_Aires';

  return {
    restaurantId,
    branchId,
    timeZone,
    branchOrdersMode: branch.ordersMode ?? null,
    branchUsesCajas: !!branch.settingUseCajas,
  };
}

/**
 * The current scope, when `scopeKey` (the scope the turn was asked in) is
 * it. A request without one (null, or absent in widgets older than host
 * contract v2), or for another restaurant/branch, never runs.
 */
export function ensureScopeKeyOrThrow(
  deps: ICartadigitalAdminHostToolDeps,
  scopeKey: string | null | undefined
): IGpthelpToolScope {
  const scope = ensureRestaurantScopeOrThrow(deps);
  const currentScopeKey = buildScopeKey(scope);
  if (scopeKey !== currentScopeKey) {
    throw new HostToolError(
      'SCOPE_MISMATCH',
      'This request was not made for the selected store and branch. Ask the user to repeat the request.',
      { expected: currentScopeKey, received: scopeKey ?? null }
    );
  }
  return scope;
}

export function ensurePermissionOrThrow(params: {
  deps: ICartadigitalAdminHostToolDeps;
  permission: PermissionType;
  message: string;
}) {
  const set = params.deps.authStore.permissionSet;
  const allowed = set?.has?.(params.permission) ?? false;
  if (!allowed) {
    throw new HostToolError('FORBIDDEN', params.message, {
      permission: PermissionType[params.permission] ?? params.permission,
    });
  }
}

/** Any-of permission gate (mirrors the server's roleAuthAny). */
export function ensureAnyPermissionOrThrow(params: {
  deps: ICartadigitalAdminHostToolDeps;
  permissions: PermissionType[];
  message: string;
}) {
  const set = params.deps.authStore.permissionSet;
  const allowed = params.permissions.some((p) => set?.has?.(p) ?? false);
  if (!allowed) {
    throw new HostToolError('FORBIDDEN', params.message, {
      permissions: params.permissions.map(
        (p) => PermissionType[p] ?? p,
      ),
    });
  }
}

/**
 * Day-close surfaces require unrestricted caja access (the server's
 * day-close-scope util throws 403 for caja-restricted members).
 */
export function ensureAllCajasOrThrow(deps: ICartadigitalAdminHostToolDeps) {
  if (!deps.authStore.currentRole?.allCajas) {
    throw new HostToolError(
      'FORBIDDEN',
      'Su rol está restringido a cajas específicas y no puede ver cierres del día',
    );
  }
}

/**
 * Shared execute-time gate for every caja tool: the branch must use cajas and
 * the user needs CajaManage or StatisticsView (the server additionally
 * filters to the member's allowed cajas).
 */
export function ensureCajaReadAccess({
  deps,
  scope,
}: Pick<IGpthelpToolExecCtx, 'deps' | 'scope'>) {
  if (!scope.branchUsesCajas) {
    throw new HostToolError('NOT_AVAILABLE', 'Esta sucursal no usa cajas');
  }

  const canSeeCaja =
    (deps.authStore.permissionSet?.has?.(PermissionType.CajaManage) ?? false) ||
    (deps.authStore.permissionSet?.has?.(PermissionType.StatisticsView) ?? false);
  if (!canSeeCaja) {
    throw new HostToolError('FORBIDDEN', 'No tiene permisos para ver cajas');
  }
}
